import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Download, Mail, Loader2, CheckCircle2, ShoppingBag } from 'lucide-react';
import Layout from '../components/Layout';
import { DownloadList } from '../components/DownloadList';
import SEO from '../components/SEO';

var API = process.env.REACT_APP_BACKEND_URL;

// Customers have no account on the site, so the library is reached with a
// private magic link emailed on request (7 days). Nothing here is guessable.
function MyDownloadsPage() {
  var searchParams = useSearchParams()[0];
  var key = searchParams.get('key');

  var [email, setEmail] = useState('');
  var [requestState, setRequestState] = useState('idle');
  var [error, setError] = useState('');
  var [library, setLibrary] = useState(null);
  var [loading, setLoading] = useState(!!key);

  var loadLibrary = useCallback(async function() {
    if (!key) return;
    setLoading(true); setError('');
    try {
      var res = await fetch(API + '/api/download/library/' + encodeURIComponent(key));
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'This link is no longer valid');
      setLibrary(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(function() { loadLibrary(); }, [loadLibrary]);

  async function requestLink(e) {
    e.preventDefault();
    var value = (email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Please enter the email address you used when ordering.');
      return;
    }
    setError(''); setRequestState('sending');
    try {
      var res = await fetch(API + '/api/download/library/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value }),
      });
      if (!res.ok) throw new Error('Something went wrong. Please try again.');
      setRequestState('sent');
    } catch (err) {
      setRequestState('idle');
      setError(err.message);
    }
  }

  return (
    <Layout>
      <SEO title="My Downloads" url="/my-downloads" noindex={true} />
      <div className="min-h-screen bg-black pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 mb-4">
            <Download className="w-4 h-4 text-[#d4af37]" />
            <span className="text-[#d4af37] text-[11px] font-semibold uppercase tracking-[0.18em]">Your library</span>
          </div>
          <h1 className="text-white text-4xl sm:text-5xl font-bold leading-tight mb-4">My downloads</h1>

          {key ? (
            <div>
              <p className="text-neutral-400 text-sm md:text-base leading-relaxed mb-10">
                {error ? 'This download link can no longer be used.'
                  : library && library.email ? 'Every file purchased with ' + library.email + '.'
                  : 'Loading your purchases…'}
              </p>
              {loading ? (
                <div className="flex items-center gap-3 text-neutral-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading your files…
                </div>
              ) : error ? (
                <div data-testid="library-error" className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
                  <p className="text-red-300 text-sm mb-4">{error}</p>
                  <Link to="/my-downloads" className="text-[#d4af37] text-sm hover:text-white transition-colors">
                    Request a new link →
                  </Link>
                </div>
              ) : library && library.files.length > 0 ? (
                <DownloadList files={library.files} libraryKey={key} />
              ) : (
                <p data-testid="library-empty" className="text-neutral-500 text-sm">
                  No digital purchases found for this email yet.
                </p>
              )}
            </div>
          ) : requestState === 'sent' ? (
            <div data-testid="library-request-sent" className="border border-[#d4af37]/30 bg-[#d4af37]/[0.06] rounded-2xl p-7">
              <CheckCircle2 className="w-8 h-8 text-[#d4af37] mb-4" />
              <h2 className="text-white text-base md:text-lg font-semibold mb-2">Check your inbox</h2>
              <p className="text-neutral-400 text-sm leading-relaxed">
                If that email has purchases on JiMendez.com, a private download link is on its way.
                It stays valid for 7 days — and you can always come back here for a fresh one.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-neutral-400 text-sm md:text-base leading-relaxed mb-8 max-w-xl">
                Enter the email you used when ordering and I'll send you a private link to every
                digital file you've bought — indexed Real Books, arrangements, everything.
                No password needed.
              </p>
              <form onSubmit={requestLink} className="max-w-md space-y-3">
                <div className="relative">
                  <Mail className="w-4 h-4 text-neutral-600 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    value={email}
                    onChange={function(e) { setEmail(e.target.value); setError(''); }}
                    placeholder="you@example.com"
                    data-testid="library-email-input"
                    className="w-full bg-neutral-900 border border-white/10 text-white text-sm rounded-xl pl-11 pr-4 py-3.5 placeholder-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors"
                  />
                </div>
                {error ? <p data-testid="library-request-error" className="text-red-400 text-xs">{error}</p> : null}
                <button
                  type="submit"
                  disabled={requestState === 'sending'}
                  data-testid="library-request-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-white text-black font-semibold text-sm rounded-full px-7 py-3.5 transition-colors disabled:opacity-60"
                >
                  {requestState === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Email me my downloads
                </button>
              </form>
              <p className="text-neutral-600 text-xs mt-8 max-w-md leading-relaxed">
                Each file allows 5 downloads within 7 days. If a link runs out, just request a new
                one — it's free and instant.
              </p>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 text-neutral-500 hover:text-[#d4af37] text-sm mt-8 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" /> Browse the shop
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default MyDownloadsPage;
